/** Pricing match by source type: match-rate trend for prepaid/collect/spot. */
import type { PricingSourceTrendPayload } from "@/types/feed";

import { carrierBookingsHref } from "../../feedLinks";
import { formatNum } from "../formatInsight";
import { explainerFor } from "../insightCopy";
import { ChangeRow, EntitySection, InsightShell } from "../primitives";

/** Capitalize a source-type tag ("collect" -> "Collect"). */
function labelSourceType(source: string): string {
  return source.charAt(0).toUpperCase() + source.slice(1);
}

export function PricingSourceTrendView({
  payload,
}: {
  payload: PricingSourceTrendPayload;
}) {
  const sources = payload.by_source_type ?? [];
  const carriers = payload.top_unmatched_carriers ?? [];

  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {sources.length > 0 && (
        <EntitySection title="Match rate by source type">
          {sources.map((s) => {
            const dropped = s.pct_point_change < 0;
            const subtitleParts = [`mix ${Math.round(s.mix_pct)}%`];
            if (s.unmatched_teu > 0) {
              subtitleParts.push(`${formatNum(s.unmatched_teu)} TEU unmatched`);
            }
            return (
              <ChangeRow
                key={s.source_type}
                name={labelSourceType(s.source_type)}
                primary={`${Math.round(s.prior_match_rate_pct)}% \u2192 ${Math.round(s.match_rate_pct)}%`}
                primaryClassName={dropped ? "text-rose-500" : "text-emerald-500"}
                subtitle={subtitleParts.join(" \u00b7 ")}
              />
            );
          })}
        </EntitySection>
      )}
      {carriers.length > 0 && (
        <EntitySection title="Unmatched carriers">
          {carriers.map((c) => (
            <ChangeRow
              key={`${c.carrier}-${c.source_type}`}
              name={c.carrier}
              href={carrierBookingsHref(c.carrier)}
              primary={`${formatNum(c.unmatched_teu)} TEU`}
              subtitle={labelSourceType(c.source_type)}
            />
          ))}
        </EntitySection>
      )}
    </InsightShell>
  );
}
